// Visual intent, client mirror.
//
// ⚠️  MIRROR — the authority is app/server/src/intelligence/figure-intent.ts.
// The VOCABULARY block below is byte-compared against the server's by
// app/server/src/intelligence/figure-intent.parity.spec.ts, so editing one and
// not the other FAILS THE BUILD. That test is the whole point of this file:
// four hand-maintained copies of this vocabulary is what caused the bug it
// exists to prevent (see the server module's header).
//
// Why a copy at all: app/web and app/server are separate npm packages (no
// workspace, and web's tsconfig `include` is ["src"]), so a cross-package import
// would need build surgery in both. A 40-line mirror with an enforcing test is
// the smaller, more honest cost — and the test makes the invariant real rather
// than a comment nobody reads.
//
// The frontend needs exactly ONE of the two decisions: the BROAD lane pin. It
// never loads skills or writes prompts, so wantsVisual() is not mirrored.

// ── Vocabulary — ONE list per concept, shared by both decisions ──────────────

/** Diagram nouns: a graph of nodes and edges. Mermaid's home ground. */
const DIAGRAM_NOUNS =
  String.raw`diagram|flow\s?chart|flowchart|flow\s?diagram|sequence\s?diagram|mind\s?map|concept\s?map` +
  String.raw`|org(?:anization|anisation)?\s?chart|erd|entity[-\s]?relationship|state\s?(?:machine|diagram)` +
  String.raw`|venn\s?diagram|class\s?diagram|swim\s?lane|gantt(?:\s?chart)?|mermaid`;

/** Data-chart nouns: rows and columns plotted. Mermaid xychart-beta / pie. */
const CHART_NOUNS =
  String.raw`bar\s?chart|line\s?chart|pie\s?chart|column\s?chart|donut\s?chart|area\s?chart` +
  String.raw`|scatter\s?(?:plot|chart)|histogram|xychart|(?:chart|graph)\s+(?:of|for|showing|comparing)`;

/**
 * FIGURE nouns — a drawn/plotted figure that is neither a graph nor a data chart:
 * a labelled shape, a geometric construction, a plotted curve. These had NO entry
 * in any of the four old regexes, which is why "figure of a right triangle" was
 * only ever recognised through the bare `diagram` noun — and therefore only ever
 * offered a flowchart. Kept to phrases with an explicit "of/for" target so bare
 * "figure" ("figure out why", "the figure was wrong") cannot trigger.
 */
const FIGURE_NOUNS = String.raw`figure\s+(?:of|for|showing)|plot\s+(?:of|for)`;

/** Verbs that ask for something to be DRAWN (as opposed to explained). */
const DRAW_VERBS = String.raw`draw|sketch|visuali[sz]e|illustrate|map\s+out|diagram`;

/**
 * Structural subjects. Gated behind a DRAW verb for wantsVisual(): bare
 * "architecture" must NOT force a diagram — "explain the architecture of Redux"
 * wants prose. couldRenderInline() accepts them bare, because there the only
 * consequence is staying on the chat lane.
 */
const STRUCTURAL_SUBJECTS =
  String.raw`architecture|infrastructure|topology|system\s?design|work\s?flow|workflow|pipeline` +
  String.raw`|data[-\s]?flow|process\s+flow`;

/**
 * Names a VISUAL file format — an editable board or a vector file. This is BOTH a
 * visual signal and a file signal: "create an excalidraw of the architecture"
 * needs no separate diagram noun, so it feeds the noun set below as well as the
 * file gate.
 *
 * Note the shape: the extension alternative sits OUTSIDE the leading `\b`. The
 * three regexes this module replaces all wrote `\b(…|\.svg\b|…)`, where the `\b`
 * can never match between a space and a `.` — so every extension branch in them
 * was DEAD ("as an .svg" never matched, only "diagram.svg" did).
 */
const VISUAL_FILE_RE = /\b(?:excalidraw|white\s?board|wireframe|svg\s+file|vector\s+file)\b|\.svg\b/i;

/** Format-neutral delivery words: the user wants a FILE, of whatever kind. Kept
 *  apart from VISUAL_FILE_RE because these say nothing about a visual — "export
 *  the report as a pdf" is a document ask. */
const FILE_DELIVERY_RE = /\b(?:editable|as\s+a\s+file|to\s+a\s+file|down\s?load|export|save\s+(?:it|this|as|to|the|a))\b|\.(?:png|pdf|html?)\b/i;

/**
 * The FILE gate — a visual is a downloadable deliverable ONLY when the user says
 * so. Everything else renders inline in the chat.
 */
export const EXPLICIT_VISUAL_FILE_RE = new RegExp(`${VISUAL_FILE_RE.source}|${FILE_DELIVERY_RE.source}`, 'i');

const VISUAL_NOUN_RE = new RegExp(
  String.raw`\b(?:${DIAGRAM_NOUNS}|${CHART_NOUNS}|${FIGURE_NOUNS})\b|${VISUAL_FILE_RE.source}`,
  'i',
);
const STRUCTURAL_RE = new RegExp(String.raw`\b(?:${STRUCTURAL_SUBJECTS})\b`, 'i');

/** Longest prompt we treat as a visual ask. Past this it is a document/spec. */
const MAX_LEN = 400;

/**
 * BROAD — could this turn be answered well by a visual rendered inline in the
 * chat? Used ONLY to pin the turn to the chat lane instead of the autonomous
 * agent (which used to drop a stray .excalidraw/.html file, or refuse outright:
 * "draw the architecture" → "I can't create the diagram").
 *
 * The chat lane renders BOTH inline formats — ```mermaid for graphs and charts,
 * ```svg for geometric/plotted figures — so pinning here no longer means
 * "mermaid or nothing", which was the routing half of the triangle bug.
 *
 * An explicit file ask returns false so genuine deliverables still go agentic.
 */
export function couldRenderInline(text: string): boolean {
  const t = (text ?? '').trim().toLowerCase();
  if (!t || t.length > MAX_LEN) return false;
  if (EXPLICIT_VISUAL_FILE_RE.test(t)) return false;
  return VISUAL_NOUN_RE.test(t) || STRUCTURAL_RE.test(t);
}

/** The mirrored vocabulary, exported for symmetry with the server module — and so
 *  DRAW_VERBS (used only by the server's wantsVisual, but mirrored because the
 *  parity test compares it) has a consumer under `noUnusedLocals`. */
export const VOCABULARY = {
  DIAGRAM_NOUNS,
  CHART_NOUNS,
  FIGURE_NOUNS,
  DRAW_VERBS,
  STRUCTURAL_SUBJECTS,
} as const;
